/**
 * Theme styles for $ref{} syntax — L2 status-aware marks and L0 pretty pill.
 */

import { EditorView } from "@codemirror/view";
import {
    CM_MONO_FONT_FAMILY,
    accentIconStyles,
    accentMutedColor,
    directivePrettyStyles,
    refMarkStyles,
    refSourceVariables,
} from "../shared/theme-helpers";

const REF_RESOLVED = "--cm-ref-resolved";
const REF_DEFINITION = "--cm-ref-definition";
const REF_SCOPE = "--cm-ref-scope";
const REF_LOOP = "--cm-ref-loop";
const REF_CONTENT = "--cm-ref-content";
const REF_ASSET = "--cm-ref-content";
const REF_PENDING = "--cm-ref-pending";
const REF_RUNNING = "--cm-ref-running";
const REF_ERROR = "--cm-ref-error";

export const rawRefMarkStyle = {
    fontStyle: "italic",
    borderRadius: "3px",
    // CodeMirror may split mark spans around cursor/completion state. Horizontal
    // padding on a split mark looks like an inserted space inside `$ref{...}`.
    paddingTop: "1px",
    paddingRight: "0",
    paddingBottom: "1px",
    paddingLeft: "0",
} as const;

export const refTheme = EditorView.baseTheme({
    ".cm-magic-ref": rawRefMarkStyle,
    ".cm-magic-ref-resolved": refMarkStyles(REF_RESOLVED, {
        backgroundPercent: 6,
        borderPercent: 0,
    }),
    ".cm-magic-ref-pending": refMarkStyles(REF_PENDING, {
        backgroundPercent: 5,
        borderPercent: 40,
        borderStyle: "dashed",
    }),
    ".cm-magic-ref-running": {
        ...refMarkStyles(REF_RUNNING, {
            backgroundPercent: 6,
            borderPercent: 40,
        }),
        animation: "cm-magic-ref-run-breathe 2s ease-in-out infinite alternate",
    },
    ".cm-magic-ref-unresolved": refMarkStyles(REF_ERROR, {
        backgroundPercent: 5,
        borderPercent: 45,
        borderStyle: "dashed",
    }),
    ".cm-magic-ref-error": refMarkStyles(REF_ERROR, {
        backgroundPercent: 8,
        borderPercent: 50,
    }),
    ".cm-magic-ref-loop-var": refMarkStyles(REF_LOOP, {
        backgroundPercent: 6,
        borderPercent: 40,
        borderStyle: "dashed",
    }),
    ".cm-magic-ref-scope-var": refMarkStyles(REF_SCOPE, {
        backgroundPercent: 6,
        borderPercent: 40,
        borderStyle: "dashed",
    }),
    ".cm-magic-ref-pretty": {
        ...directivePrettyStyles(REF_DEFINITION, {
            gap: "5px",
            cursor: "pointer",
            transition: "background-color 0.15s, opacity 0.15s",
        }),
        "--cm-magic-ref-primary-color": `var(${REF_DEFINITION})`,
    },
    ".cm-line.cm-magic-ref-heading-line": {
        whiteSpace: "pre !important",
        wordBreak: "normal !important",
        overflowWrap: "normal !important",
    },
    ".cm-line.cm-magic-ref-heading-line .cm-magic-ref-pretty": {
        whiteSpace: "nowrap",
    },
    ".cm-magic-ref-pretty-resolved": {
        backgroundColor: "transparent",
    },
    ".cm-magic-ref-pretty-unresolved": {
        backgroundColor: "transparent",
    },
    ".cm-magic-ref-pretty-error": {
        backgroundColor: "transparent",
    },
    ".cm-magic-ref-pretty-loop-var": {
        backgroundColor: "transparent",
    },
    ".cm-magic-ref-pretty-scope-var": {
        backgroundColor: "transparent",
    },
    ".cm-magic-ref-pretty-icon": {
        flexShrink: "0",
    },
    ".cm-magic-ref-pretty-source-loop-var .cm-magic-ref-pretty-icon": accentIconStyles(
        REF_LOOP,
    ),
    ".cm-magic-ref-pretty-source-loop-var .cm-magic-ref-pretty-id": {
        color: `var(${REF_LOOP})`,
    },
    ".cm-magic-ref-pretty-source-loop-var": refSourceVariables(REF_LOOP),
    ".cm-magic-ref-pretty-source-scope-var .cm-magic-ref-pretty-icon": accentIconStyles(
        REF_SCOPE,
    ),
    ".cm-magic-ref-pretty-source-scope-var .cm-magic-ref-pretty-id": {
        color: `var(${REF_SCOPE})`,
    },
    ".cm-magic-ref-pretty-source-scope-var": refSourceVariables(REF_SCOPE),
    ".cm-magic-ref-pretty-source-definition .cm-magic-ref-pretty-icon":
        accentIconStyles(REF_DEFINITION),
    ".cm-magic-ref-pretty-source-definition .cm-magic-ref-pretty-plugin": {
        color: `var(${REF_DEFINITION})`,
        fontWeight: "600",
        /* letterSpacing removed — caused visual spacing artifacts with $ref widgets */
    },
    ".cm-magic-ref-pretty-source-definition .cm-magic-ref-pretty-id": {
        color: `color-mix(in oklab, var(${REF_DEFINITION}) 86%, var(--color-foreground))`,
    },
    ".cm-magic-ref-pretty-source-definition .cm-magic-ref-pretty-path, .cm-magic-ref-pretty-source-definition .cm-magic-ref-pretty-sep, .cm-magic-ref-pretty-source-definition .cm-magic-ref-pretty-eq":
        {
            color: accentMutedColor(REF_DEFINITION, 78, "var(--color-foreground)"),
        },
    ".cm-magic-ref-pretty-source-definition .cm-magic-ref-pretty-value": {
        color: `color-mix(in oklab, var(${REF_DEFINITION}) 72%, var(--color-foreground))`,
    },
    ".cm-magic-ref-pretty-source-definition": refSourceVariables(
        REF_DEFINITION,
        `color-mix(in oklab, var(${REF_DEFINITION}) 86%, var(--color-foreground))`,
    ),
    ".cm-magic-ref-pretty-source-content .cm-magic-ref-pretty-icon":
        accentIconStyles(REF_CONTENT),
    ".cm-magic-ref-pretty-source-content .cm-magic-ref-pretty-id, .cm-magic-ref-pretty-source-content .cm-magic-ref-pretty-path, .cm-magic-ref-pretty-source-content .cm-magic-ref-pretty-sep, .cm-magic-ref-pretty-source-content .cm-magic-ref-pretty-eq, .cm-magic-ref-pretty-source-content .cm-magic-ref-pretty-value":
        {
            color: `var(${REF_CONTENT})`,
        },
    ".cm-magic-ref-pretty-source-content": refSourceVariables(REF_CONTENT),
    ".cm-magic-ref-pretty-source-asset .cm-magic-ref-pretty-icon":
        accentIconStyles(REF_ASSET),
    ".cm-magic-ref-pretty-source-asset .cm-magic-ref-pretty-id, .cm-magic-ref-pretty-source-asset .cm-magic-ref-pretty-path, .cm-magic-ref-pretty-source-asset .cm-magic-ref-pretty-sep, .cm-magic-ref-pretty-source-asset .cm-magic-ref-pretty-eq, .cm-magic-ref-pretty-source-asset .cm-magic-ref-pretty-value":
        {
            color: `var(${REF_ASSET})`,
        },
    ".cm-magic-ref-pretty-source-asset": refSourceVariables(REF_ASSET),
    ".cm-magic-ref-pretty-source-for .cm-magic-ref-pretty-icon":
        accentIconStyles(REF_PENDING),
    ".cm-magic-ref-pretty-source-for .cm-magic-ref-pretty-id, .cm-magic-ref-pretty-source-for .cm-magic-ref-pretty-path, .cm-magic-ref-pretty-source-for .cm-magic-ref-pretty-sep, .cm-magic-ref-pretty-source-for .cm-magic-ref-pretty-eq, .cm-magic-ref-pretty-source-for .cm-magic-ref-pretty-value":
        {
            color: `var(${REF_PENDING})`,
        },
    ".cm-magic-ref-pretty-source-for": refSourceVariables(REF_PENDING),
    ".cm-magic-ref-pretty-error .cm-magic-ref-pretty-id, .cm-magic-ref-pretty-error .cm-magic-ref-pretty-path, .cm-magic-ref-pretty-error .cm-magic-ref-pretty-sep, .cm-magic-ref-pretty-error .cm-magic-ref-pretty-eq, .cm-magic-ref-pretty-error .cm-magic-ref-pretty-plugin, .cm-magic-ref-pretty-error .cm-magic-ref-pretty-value":
        {
            color: `var(${REF_ERROR})`,
        },
    ".cm-magic-ref-pretty-unresolved .cm-magic-ref-pretty-id, .cm-magic-ref-pretty-unresolved .cm-magic-ref-pretty-path, .cm-magic-ref-pretty-unresolved .cm-magic-ref-pretty-sep, .cm-magic-ref-pretty-unresolved .cm-magic-ref-pretty-eq, .cm-magic-ref-pretty-unresolved .cm-magic-ref-pretty-plugin":
        {
            color: `var(${REF_ERROR})`,
        },
    ".cm-magic-ref-pretty-error .cm-magic-ref-pretty-icon": accentIconStyles(
        REF_ERROR,
    ),
    ".cm-magic-ref-pretty-unresolved .cm-magic-ref-pretty-icon":
        accentIconStyles(REF_ERROR),
    ".cm-magic-ref-pretty-error, .cm-magic-ref-pretty-unresolved":
        refSourceVariables(REF_ERROR),
    ".cm-magic-ref-pretty-running": refSourceVariables(REF_RUNNING),
    ".cm-magic-ref-pretty-pending": refSourceVariables(REF_PENDING),
    ".cm-magic-ref-pretty-id, .cm-magic-ref-pretty-path, .cm-magic-ref-pretty-sep, .cm-magic-ref-pretty-eq":
        {
            color: `var(--cm-magic-ref-primary-color, var(${REF_DEFINITION}))`,
            fontSize: "12px",
        },
    ".cm-magic-ref-pretty-sep, .cm-magic-ref-pretty-eq": {
        opacity: "0.5",
    },
    ".cm-magic-ref-pretty-path": {
        fontFamily: CM_MONO_FONT_FAMILY,
    },
    ".cm-magic-ref-pretty-value": {
        color: `var(--cm-magic-ref-primary-color, var(${REF_DEFINITION}))`,
        fontSize: "12px",
        fontWeight: "700",
        fontFamily: CM_MONO_FONT_FAMILY,
        maxWidth: "180px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
});
